import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import RccCard from "../RccCard";
import { rccColors } from "../colors";
import { useRccTypography } from "../typography";
import useFreshNewQualifiedPipelineData from "../hooks/useFreshNewQualifiedPipelineData";
import {
  countOrACVFormatterThousand,
  customDateFormatter,
  getUniqueValues,
} from "../../util/customFunctions";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    width: "100%",
    height: "100%",
    backgroundColor: "#FFFFFF",
    overflowX: "hidden",
    border: `1px solid ${rccColors.materialGrey ? rccColors.materialGrey[200] : "#EEEEEE"}`,
    borderRadius: "4px 4px 0 0",
    [theme.breakpoints.down("md")]: {
      overflowX: "auto",
    },
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "auto",
  },
  headerCell: {
    padding: "8px 12px",
    borderBottom: `1px solid ${rccColors.materialGrey ? rccColors.materialGrey[300] : "#E0E0E0"}`,
    borderRight: `1px solid ${rccColors.materialGrey ? rccColors.materialGrey[300] : "#E0E0E0"}`,
    textAlign: "center",
  },
  countHeaderCell: {
    minWidth: "60px",
    width: "60px",
    color: rccColors.neutral[800],
  },
  valueHeaderCell: {
    maxWidth: "80px",
    width: "80px",
    whiteSpace: "normal",
    lineHeight: 1.2,
  },
  countDataCell: {
    minWidth: "60px",
    width: "60px",
    color: rccColors.neutral[800],
  },
  valueDataCell: {
    maxWidth: "80px",
    width: "80px",
  },
  goalHeaderCell: {
    minWidth: "80px",
    width: "80px",
  },
  rowHeaderCell: {
    backgroundColor: "#FFFFFF",
    color: rccColors.neutral ? rccColors.neutral[600] : "#333",
    padding: "8px 12px",
    borderBottom: `1px solid ${rccColors.materialGrey ? rccColors.materialGrey[200] : "#e0e0e0"}`,
    borderRight: `1px solid ${rccColors.materialGrey ? rccColors.materialGrey[200] : "#e0e0e0"}`,
    textAlign: "left",
    minWidth: "170px",
    width: "170px",
  },
  whiteHeader: {
    backgroundColor: rccColors.materialGrey
      ? rccColors.materialGrey[100]
      : "#F5F5F5",
    color: rccColors.neutral ? rccColors.neutral[600] : "#78909C",
  },
  blueHeader700: {
    backgroundColor: rccColors.blue ? rccColors.blue[700] : "#1976D2",
    color: "#FFFFFF",
  },
  blueHeader400: {
    backgroundColor: rccColors.blue ? rccColors.blue[400] : "#42A5F5",
    color: "#FFFFFF",
  },
  dataCell: {
    padding: "8px 12px",
    borderBottom: `1px solid ${rccColors.materialGrey ? rccColors.materialGrey[200] : "#e0e0e0"}`,
    borderRight: `1px solid ${rccColors.materialGrey ? rccColors.materialGrey[200] : "#e0e0e0"}`,
    textAlign: "right",
    color: rccColors.neutral ? rccColors.neutral[800] : "#555",
  },
  teamName: {
    color: rccColors.neutral[800],
  },
  repName: {
    paddingLeft: "1.75rem",
    color: rccColors.neutral[800],
  },
  greenCell: {
    backgroundColor: rccColors.green ? rccColors.green[100] : "#C8E6C9",
    color: rccColors.neutral ? rccColors.neutral[800] : "#333",
  },
  orangeCell: {
    backgroundColor: rccColors.orange ? rccColors.orange[200] : "#FFE0B2",
    color: rccColors.neutral ? rccColors.neutral[800] : "#333",
  },
}));

const FreshNewQualifiedPipelineCard = ({ title, redirectToDashboard }) => {
  const classes = useStyles();
  const typography = useRccTypography();
  const Value_Label =
    useSelector((state) => state.app.user?.Value_Label) || "Value";
  const { data, loading, error, selectedTeamName, appliedFilters } =
    useFreshNewQualifiedPipelineData();

  if (error) {
    return (
      <RccCard title="Fresh New Qualified Pipeline created by week">
        <Typography color="textSecondary" style={{ padding: "1rem" }}>
          Unable to load data. Please try again later.
        </Typography>
      </RccCard>
    );
  }

  const trailingFourWeeks = Array.isArray(data?.trailingFourWeeks)
    ? [...data.trailingFourWeeks].sort()
    : [];
  const trailingFourWeeksData = Array.isArray(data?.trailingFourWeeksData)
    ? data.trailingFourWeeksData
    : [];
  const trailingEightWeeksAverage = Array.isArray(
    data?.trailingEightWeeksAverage,
  )
    ? data.trailingEightWeeksAverage
    : [];
  const traillingFourWeeksDataSegment = Array.isArray(
    data?.traillingFourWeeksDataSegment,
  )
    ? data.traillingFourWeeksDataSegment
    : [];
  const traillingEightWeeksSegmentAverage =
    data?.trailingEightWeeksAverageSegment ||
    data?.traillingFourWeeksSegmentAverage ||
    {};

  const frequencyChurnedStageChangeColumn =
    data?.frequencyChurnedStageChangeColumn || "Week_Ending";
  const firstColumn = data?.UILabelFirstColumn || "Week Ending";
  const thresholdFlag = data?.thresholdFlag;
  const showOrange = data?.showOrange !== false;
  const acvColumn = "currentACV";

  const repOrTeamKey = trailingFourWeeksData.some((r) => r.Owner_Name != null)
    ? "Owner_Name"
    : "Team";

  const uniqueReps = trailingFourWeeksData
    .map((row) => row[repOrTeamKey])
    .filter((name) => name && typeof name === "string" && name.trim() !== "")
    .filter(getUniqueValues)
    .sort();

  const weekTs = (w) =>
    w instanceof Date ? w.getTime() : new Date(w).getTime();

  const getCellStyle = (goal, acv) => {
    if (showOrange && acv <= 0) return classes.orangeCell;
    if (goal != null && goal > 0 && acv >= goal) return classes.greenCell;
    return "";
  };

  const teamDisplayName =
    selectedTeamName ||
    data?.teamsFromFilterForCompute?.[0] ||
    (traillingFourWeeksDataSegment.length > 0 &&
      traillingFourWeeksDataSegment[0]?.Segment);

  return (
    <RccCard
      title={title || "Fresh New Qualified Pipeline created by week"}
      isLoading={!!loading}
      redirectToDashboard={redirectToDashboard || "Performance Scorecard"}
      appliedFilters={appliedFilters}
      focusVariant={"future"}
    >
      {trailingFourWeeks.length === 0 ? (
        <Typography color="textSecondary" style={{ padding: "1rem" }}>
          No data available for the selected team.
        </Typography>
      ) : (
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  className={`${classes.rowHeaderCell} ${classes.whiteHeader} ${typography.textExtraSmallSemibold}`}
                >
                  {firstColumn}
                </TableCell>
                {thresholdFlag && showOrange && (
                  <TableCell
                    className={`${classes.headerCell} ${classes.whiteHeader} ${classes.goalHeaderCell} ${typography.textExtraSmallSemibold}`}
                    align="center"
                  >
                    Goal
                  </TableCell>
                )}
                {trailingFourWeeks.map((week, index) => (
                  <TableCell
                    key={`wk-${index}`}
                    colSpan={2}
                    className={`${classes.headerCell} ${index % 2 === 0 ? classes.blueHeader700 : classes.blueHeader400} ${typography.textExtraSmallSemibold}`}
                    align="center"
                  >
                    {customDateFormatter(week)}
                  </TableCell>
                ))}
                <TableCell
                  colSpan={2}
                  className={`${classes.headerCell} ${classes.blueHeader700} ${typography.textExtraSmallSemibold}`}
                  align="center"
                >
                  Trailing 8-weeks Avg.
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  className={`${classes.rowHeaderCell} ${typography.textExtraSmallSemibold}`}
                ></TableCell>
                {thresholdFlag && showOrange && (
                  <TableCell
                    className={`${classes.headerCell} ${classes.goalHeaderCell} ${typography.textExtraSmallSemibold}`}
                    align="center"
                  ></TableCell>
                )}
                {trailingFourWeeks.map((week, index) => (
                  <React.Fragment key={`subheader-${index}`}>
                    <TableCell
                      className={`${classes.headerCell} ${classes.countHeaderCell} ${typography.textExtraSmallRegular}`}
                      align="right"
                    >
                      #
                    </TableCell>
                    <TableCell
                      className={`${classes.headerCell} ${classes.valueHeaderCell} ${typography.textExtraSmallRegular}`}
                      align="center"
                    >
                      Current {Value_Label}
                    </TableCell>
                  </React.Fragment>
                ))}
                <TableCell
                  className={`${classes.headerCell} ${classes.countHeaderCell} ${typography.textExtraSmallRegular}`}
                  align="right"
                >
                  #
                </TableCell>
                <TableCell
                  className={`${classes.headerCell} ${classes.valueHeaderCell} ${typography.textExtraSmallRegular}`}
                  align="center"
                >
                  Current {Value_Label}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Team Row */}
              <TableRow className={typography.textSmallRegular}>
                <TableCell
                  component="th"
                  scope="row"
                  className={classes.rowHeaderCell}
                >
                  <Typography
                    className={`${classes.teamName} ${typography.textSmallSemibold}`}
                  >
                    {teamDisplayName}
                  </Typography>
                </TableCell>
                {thresholdFlag && showOrange && (
                  <TableCell
                    className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.goalDataCell}`}
                    align="right"
                  >
                    {traillingFourWeeksDataSegment.length > 0
                      ? countOrACVFormatterThousand(
                          traillingFourWeeksDataSegment[0].nqpGoal,
                          true,
                        )
                      : "0"}
                  </TableCell>
                )}
                {trailingFourWeeks.map((week) => {
                  const seg =
                    traillingFourWeeksDataSegment.find(
                      (item) =>
                        weekTs(item[frequencyChurnedStageChangeColumn]) ===
                        weekTs(week),
                    ) || {};
                  const count = seg.count ?? 0;
                  const acv = seg[acvColumn] ?? 0;
                  const goal = seg.nqpGoal;
                  return (
                    <React.Fragment key={String(week)}>
                      <TableCell
                        className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.countDataCell}`}
                        align="right"
                      >
                        {countOrACVFormatterThousand(count, false)}
                      </TableCell>
                      <TableCell
                        className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.valueDataCell} ${getCellStyle(goal, acv)}`}
                        align="right"
                      >
                        {countOrACVFormatterThousand(acv, true)}
                      </TableCell>
                    </React.Fragment>
                  );
                })}
                <TableCell
                  className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.countDataCell}`}
                  align="right"
                >
                  {Number(traillingEightWeeksSegmentAverage.count ?? 0).toFixed(
                    1,
                  )}
                </TableCell>
                <TableCell
                  className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.valueDataCell} ${getCellStyle(traillingEightWeeksSegmentAverage.nqpGoal, traillingEightWeeksSegmentAverage[acvColumn])}`}
                  align="right"
                >
                  {countOrACVFormatterThousand(
                    traillingEightWeeksSegmentAverage[acvColumn] ?? 0,
                    true,
                  )}
                </TableCell>
              </TableRow>

              {/* Rep Rows */}
              {uniqueReps.map((rep) => {
                const averageData = trailingEightWeeksAverage.find(
                  (item) => item[repOrTeamKey] === rep,
                ) || { acv: 0, nqpGoal: 0, [acvColumn]: 0, count: 0 };
                const goalsForRep = trailingFourWeeksData.filter(
                  (item) => item[repOrTeamKey] === rep,
                );
                const goal =
                  goalsForRep.length > 0 ? goalsForRep[0].nqpGoal : 0;

                return (
                  <TableRow key={rep} className={typography.textSmallRegular}>
                    <TableCell
                      component="th"
                      scope="row"
                      className={classes.rowHeaderCell}
                    >
                      <Typography
                        className={`${classes.repName} ${typography.textSmallRegular}`}
                      >
                        {rep}
                      </Typography>
                    </TableCell>
                    {thresholdFlag && showOrange && (
                      <TableCell
                        className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.goalDataCell}`}
                        align="right"
                      >
                        {countOrACVFormatterThousand(goal, true)}
                      </TableCell>
                    )}
                    {trailingFourWeeks.map((week) => {
                      const cellData = trailingFourWeeksData.find(
                        (item) =>
                          weekTs(item[frequencyChurnedStageChangeColumn]) ===
                            weekTs(week) && item[repOrTeamKey] === rep,
                      ) || { acv: 0, nqpGoal: 0, [acvColumn]: 0, count: 0 };
                      return (
                        <React.Fragment key={String(week)}>
                          <TableCell
                            className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.countDataCell}`}
                            align="right"
                          >
                            {countOrACVFormatterThousand(
                              cellData.count ?? 0,
                              false,
                            )}
                          </TableCell>
                          <TableCell
                            className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.valueDataCell} ${getCellStyle(cellData.nqpGoal, cellData[acvColumn])}`}
                            align="right"
                          >
                            {countOrACVFormatterThousand(
                              cellData[acvColumn],
                              true,
                            )}
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                    <TableCell
                      className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.countDataCell}`}
                      align="right"
                    >
                      {Number(averageData.count ?? 0).toFixed(1)}
                    </TableCell>
                    <TableCell
                      className={`${classes.dataCell} ${typography.textSmallRegular} ${classes.valueDataCell} ${getCellStyle(averageData.nqpGoal, averageData[acvColumn])}`}
                      align="right"
                    >
                      {countOrACVFormatterThousand(
                        averageData[acvColumn] ?? 0,
                        true,
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </RccCard>
  );
};

export default FreshNewQualifiedPipelineCard;
